import { isValid, parseISO } from 'date-fns'
import { redirect } from 'react-router-dom'
import { fetchMyCalendarItems } from '../apis/advocate'
import { fetchMyCalendarAccesses, fetchSharedCalendar } from '../apis/calendar-sharing'
import { fetchPost, fetchPostByShareToken, fetchSocialMediaShareForPost, resumeShare } from '../apis/post'
import { fetchMyCommentTargets } from '../apis/comments'
import { fetchSocialMediaShareByID } from '../apis/social-media-shares'
import { CALENDAR_ACCESS_STATUS, NOW, NOW_MINUS_1_WEEK } from '../constants'
import { calendarRange, CALENDAR_VIEW } from '../helpers/calendar'
import { advocatePostsPagePath } from '../links'

export async function loadSharePostPageData({ params, request }) {
  const postID = params.postID
  const orgID = new URL(request.url).searchParams.get('orgID')
  const [{ post, error }, { socialMediaShare }] = await Promise.all([
    fetchPost({ postID, orgID }),
    fetchSocialMediaShareForPost({ postID, orgID }),
  ])
  if (error) throw new Error(error)
  return { post, socialMediaShare }
}

export async function loadApprovePostPageData({ params, request }) {
  const postID = params.postID
  const orgID = new URL(request.url).searchParams.get('orgID')
  const { post, error } = await fetchPost({ postID, orgID })
  if (error) throw new Error(error)
  return { post }
}

export async function loadAdvocatePostsPageData({ request }) {
  const status = new URL(request.url).searchParams.get('status') || "NEW"
  let channelIDs = new URL(request.url).searchParams.getAll('channelIDs') || []
  channelIDs = channelIDs.map(id => parseInt(id)).filter(id => Number.isInteger(id))
  let campaignIDs = new URL(request.url).searchParams.getAll('campaignIDs')
  campaignIDs = campaignIDs.map(id => parseInt(id)).filter(id => Number.isInteger(id))

  return { shareStatus: status, filters: { channelIDs, campaignIDs } }
}

export function loadAdvocateAnalyticsPageData({ request }) {
  const url = new URL(request.url)
  const start = url.searchParams.get('start') || NOW_MINUS_1_WEEK
  const end = url.searchParams.get('end') || NOW
  return { start, end }
}

export async function loadExternalSharePostPageData({ params }) {
  const shareTokenID = params.shareTokenID
  const { post, socialMediaShare, organization, error } = await fetchPostByShareToken({ shareTokenID })
  if (error) throw new Error(error)
  return { post, socialMediaShare, organization }
}

export async function loadSocialMediaShareDetailsPageData({ params, request }) {
  const shareID = params.shareID
  const orgID = new URL(request.url).searchParams.get('orgID')
  const { share, error } = await fetchSocialMediaShareByID({ shareID, orgID })
  if (error) throw new Error(error)
  const { post, error: postError } = await fetchPost({ postID: share.PostID, orgID })
  if (postError) throw new Error(postError)
  return { share, post }
}

export async function loadExternalSharePostPageResumeData({ params }) {
  const shareTokenID = params.shareTokenID
  const { error } = await resumeShare({ shareTokenID })
  if (error) throw new Error(error)

  throw redirect("/s/" + shareTokenID, { replace: true })
}


export async function loadAdvocateCommentsPageData({ request }) {
  const orgID = new URL(request.url).searchParams.get('orgID')
  const { commentTargets, error, errorCode } = await fetchMyCommentTargets({ orgID })
  // comments are not enabled for the organization
  if (errorCode === 422) {
    return redirect(advocatePostsPagePath(orgID))
  }
  if (error) throw new Error(error)
  return { commentTargets }
}

// calendarParams reads the org, the month or week view, and the day shown from the url of a calendar page
function calendarParams(request) {
  const url = new URL(request.url)
  const orgID = url.searchParams.get('orgID')
  const view = url.searchParams.get('view') === CALENDAR_VIEW.WEEK ? CALENDAR_VIEW.WEEK : CALENDAR_VIEW.MONTH
  const parsedDate = parseISO(url.searchParams.get('date') || "")
  const date = isValid(parsedDate) ? parsedDate : new Date()
  return { orgID, view, date, ...calendarRange(view, date) }
}

// loadAdvocateCalendarData combines the advocate's items with the items of the calendars shared with them, each marked with its Owner
export async function loadAdvocateCalendarData({ request }) {
  const { orgID, view, date, from, to } = calendarParams(request)
  const [{ items, error }, { accesses, error: accessesError }] = await Promise.all([
    fetchMyCalendarItems({ orgID, from, to }),
    fetchMyCalendarAccesses({ orgID }),
  ])
  if (error) throw new Error(error)
  if (accessesError) throw new Error(accessesError)

  const sharedCalendars = accesses.filter(a => a.Status === CALENDAR_ACCESS_STATUS.GRANTED && a.CalendarInfo).map(a => a.CalendarInfo)
  const sharedResults = await Promise.all(sharedCalendars.map(calendar => fetchSharedCalendar({ orgID, calendar: calendar.Calendar, from, to })))
  // a calendar whose access was removed meanwhile is left out rather than failing the page
  const sharedItems = sharedResults.flatMap(({ items, error }, i) => {
    if (error) {
      console.error("error while loading a shared calendar", sharedCalendars[i].Calendar, error)
      return []
    }
    return items.map(item => ({ ...item, Owner: sharedCalendars[i] }))
  })
  const combinedItems = [...items, ...sharedItems].sort((a, b) => new Date(a.At) - new Date(b.At))
  return { items: combinedItems, sharedCalendars, accesses, view, date }
}

// sharedCalendarData returns the calendar, or noAccess when the user can't see it
async function sharedCalendarData(orgID, view, date, fetchCalendar, calendarRef) {
  const [{ calendar, items, error, status }, { accesses, error: accessesError }] = await Promise.all([
    fetchCalendar(),
    fetchMyCalendarAccesses({ orgID }),
  ])
  if (accessesError) throw new Error(accessesError)
  if (status === 401 || status === 404) {
    return { noAccess: true, accesses, view, date, calendarRef }
  }
  if (error) throw new Error(error)
  return { calendar, items, accesses, view, date, calendarRef: calendarRef || calendar.Calendar }
}

export async function loadSharedCalendarData({ params, request }) {
  const { orgID, view, date, from, to } = calendarParams(request)
  const calendarRef = { Type: params.type, ID: Number(params.id) }
  return sharedCalendarData(orgID, view, date, () => fetchSharedCalendar({ orgID, calendar: calendarRef, from, to }), calendarRef)
}

